import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { api } from '../api';
import { t } from '../i18n';
import type { Me } from '../App';
import { usePatchnotes } from './Patchnotes';
import { SaveStatusIndicator } from './ui';

interface VersionInfo {
  version: string;
  commit: string;
  builtAt: string | null;
  calcCore: string;
}

/**
 * Auslieferungsstand in der Fusszeile (D-057). Die Angabe kommt von der laufenden
 * API, nicht aus dem Web-Bau — so zeigt sie den Stand, der die Daten tatsächlich
 * verarbeitet, und verrät zugleich, wenn nach einem Ausrollen der alte Stand läuft.
 */
function Version() {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  useEffect(() => {
    api
      .get<VersionInfo>('/api/v1/meta/version')
      .then(setInfo)
      .catch(() => setInfo(null));
  }, []);
  if (!info) return null;
  const titel = [
    `Rechenkern ${info.calcCore}`,
    info.builtAt ? `gebaut ${new Date(info.builtAt).toLocaleString('de-CH')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <span style={{ marginLeft: '0.75rem' }} title={titel}>
      {t('app.version', { version: info.version, commit: info.commit })}
    </span>
  );
}

export function Layout({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const patchnotes = usePatchnotes(me.userId);
  return (
    <>
      {patchnotes.dialog}
      <header className="app-kopf">
        <Link to="/" aria-label="WerkDelta — Startseite">
          <img src="/logo-invers.svg" alt="WerkDelta" />
        </Link>
        <nav aria-label="Hauptnavigation">
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'aktiv' : '')}>
            {t('nav.cases')}
          </NavLink>
          <NavLink to="/info" className={({ isActive }) => (isActive ? 'aktiv' : '')}>
            {t('nav.info')}
          </NavLink>
          <NavLink to="/glossar" className={({ isActive }) => (isActive ? 'aktiv' : '')}>
            {t('glossary.title')}
          </NavLink>
          {me.role === 'ADMIN' && (
            <NavLink to="/admin/benutzer" className={({ isActive }) => (isActive ? 'aktiv' : '')}>
              {t('nav.users')}
            </NavLink>
          )}
          <NavLink to="/konto" className={({ isActive }) => (isActive ? 'aktiv' : '')}>
            {me.displayName}
          </NavLink>
          <button
            type="button"
            className="kopf-aktion"
            onClick={async () => {
              await api.post('/api/v1/auth/logout').catch(() => undefined);
              onLogout();
            }}
          >
            {t('nav.logout')}
          </button>
        </nav>
      </header>
      {me.organization.isDemo && <div className="demo-banner">{t('app.demo.banner')}</div>}
      {me.organization.licenseExpired && (
        <div className="warnung" style={{ margin: '0.6rem 1.25rem' }}>
          {t('license.expired.readonly')}
        </div>
      )}
      <main className="inhalt">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SaveStatusIndicator />
        </div>
        <Outlet />
      </main>
      <footer className="app-fuss">
        <span>{t('app.footer')}</span>
        <Version />
        <button type="button" className="fuss-link" onClick={patchnotes.oeffneAlle}>
          {t('patchnotes.fussLink')}
        </button>
        <span style={{ marginLeft: '0.75rem' }}>
          <Link to="/impressum">{t('app.impressumLink')}</Link>
          {' · '}
          <Link to="/nutzungsbedingungen">{t('app.nutzungLink')}</Link>
          {' · '}
          <Link to="/datenschutz">{t('app.datenschutzLink')}</Link>
        </span>
      </footer>
    </>
  );
}
